import { supabase } from '../../api/supabase'
import type { CheckId, CheckOverride, OverrideCheckType, OverrideStatus } from './types'

export function toOverrideCheckType(id: CheckId): OverrideCheckType {
  return id === 'hs_codes' ? 'hs_code' : id
}

interface OverrideRow {
  id: string
  verification_run_id: string
  check_type: OverrideCheckType
  override_status: OverrideStatus
  notes: string
  staff_email: string
  created_at: string
}

function fromRow(row: OverrideRow): CheckOverride {
  return {
    id: row.id,
    verificationRunId: row.verification_run_id,
    checkType: row.check_type,
    overrideStatus: row.override_status,
    notes: row.notes,
    staffEmail: row.staff_email,
    createdAt: row.created_at,
  }
}

export async function fetchOverrides(verificationRunId: string): Promise<CheckOverride[]> {
  const { data, error } = await supabase
    .from('verification_check_overrides')
    .select('*')
    .eq('verification_run_id', verificationRunId)

  if (error) throw new Error(`Failed to fetch overrides: ${error.message}`)
  return ((data ?? []) as OverrideRow[]).map(fromRow)
}

// Upserts on (verification_run_id, check_type) so a staff member can change
// their mind (Accept -> Reject or vice versa) without creating duplicate rows.
export async function saveOverride(
  verificationRunId: string,
  checkType: OverrideCheckType,
  overrideStatus: OverrideStatus,
  notes: string,
  staffEmail: string
): Promise<CheckOverride> {
  const { data, error } = await supabase
    .from('verification_check_overrides')
    .upsert(
      {
        verification_run_id: verificationRunId,
        check_type: checkType,
        override_status: overrideStatus,
        notes,
        staff_email: staffEmail,
      },
      { onConflict: 'verification_run_id,check_type' }
    )
    .select('*')
    .single()

  if (error) throw new Error(`Failed to save override: ${error.message}`)
  return fromRow(data as OverrideRow)
}
